import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

export interface TranslationModel {
  languageCode: string;
  fieldName: string;
  translatedValue: string;
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  private translations: { [key: string]: { [key: string]: string } } = {};
  private translationsLoaded = false;

  // Enhanced caching and memory management
  private translationCache = new Map<string, string>();
  private loadingPromises = new Map<string, Promise<void>>();
  private readonly MAX_CACHE_SIZE = 1000;
  private cacheHits = 0;
  private cacheMisses = 0;

  private readonly supportedLanguages: Language[] = [
    { code: 'en', name: 'English', flag: 'assets/images/flags/en.jpg' },
    { code: 'fr', name: 'Français', flag: 'assets/images/flags/fr.jpg' },
    { code: 'de', name: 'Deutsch', flag: 'assets/images/flags/de.jpg' },
    { code: 'it', name: 'Italiano', flag: 'assets/images/flags/it.jpg' },
  ];

  constructor(private storageService: StorageService) {
    this.initializeTranslations();
  }

  /**
   * Initialize translations - load the saved language immediately
   */
  private async initializeTranslations(): Promise<void> {
    try {
      // Clear expired items first
      // this.storageService.clearExpiredItems();

      // Get saved language with validation
      const savedLanguage = this.getSavedLanguage();

      // Set the current language
      this.currentLanguageSubject.next(savedLanguage);
      sessionStorage.setItem('lang', savedLanguage);
      document.documentElement.lang = savedLanguage;

      // Load the saved language immediately to avoid timing issues
      await this.loadLanguageFile(savedLanguage);

      this.translationsLoaded = true; // Mark as initialized

      console.log(
        `🚀 Translation service initialized for ${savedLanguage} with translations loaded`
      );
    } catch (error) {
      console.error('Error initializing translations:', error);
      this.translationsLoaded = true;
    }
  }

  /**
   * Load translation file for a specific language with duplicate prevention
   */
  private async loadLanguageFile(languageCode: string): Promise<void> {
    // Check if already loaded
    if (this.translations[languageCode]) {
      return;
    }

    // Check if already loading
    if (this.loadingPromises.has(languageCode)) {
      return this.loadingPromises.get(languageCode)!;
    }

    // Create loading promise
    const loadingPromise = this.performLanguageLoad(languageCode);
    this.loadingPromises.set(languageCode, loadingPromise);

    try {
      await loadingPromise;
    } finally {
      this.loadingPromises.delete(languageCode);
    }
  }

  /**
   * Perform the actual language file loading
   */
  private async performLanguageLoad(languageCode: string): Promise<void> {
    try {
      const response = await fetch(`/assets/i18n/${languageCode}.json`);
      if (response.ok) {
        this.translations[languageCode] = await response.json();
        console.log(`✅ Loaded translations for ${languageCode}`);
      } else {
        console.warn(`Translation file for ${languageCode} not found`);
        this.translations[languageCode] = {};
      }
    } catch (error) {
      console.error(
        `Error loading translation file for ${languageCode}:`,
        error
      );
      this.translations[languageCode] = {};
    }
  }

  /**
   * Get translation for a key in the current language with auto-loading and caching
   */
  translate(key: string, params?: { [key: string]: string | number }): string {
    if (!this.translationsLoaded) {
      return key; // Return key if service not initialized
    }

    const currentLang = this.currentLanguageSubject.value;
    const cacheKey = `${currentLang}:${key}:${JSON.stringify(params || {})}`;

    // Check cache first
    if (this.translationCache.has(cacheKey)) {
      this.cacheHits++;
      return this.translationCache.get(cacheKey)!;
    }

    this.cacheMisses++;

    // Auto-load language file if not loaded
    if (!this.translations[currentLang]) {
      // Trigger async loading but return key immediately for first render
      this.loadLanguageFile(currentLang).then(() => {
        // After loading, clear cache and trigger re-render
        this.clearTranslationCache();
        this.currentLanguageSubject.next(currentLang); // Trigger pipe updates
      });

      // Return key for immediate render
      return key;
    }

    const translation = this.translations[currentLang]?.[key] || key;

    let result: string;
    // Replace parameters in translation
    if (params) {
      result = this.replaceParams(translation, params);
    } else {
      result = translation;
    }

    // Cache the result (with size management)
    this.setCachedTranslation(cacheKey, result);

    return result;
  }

  /**
   * Replace parameters in translation string
   */
  private replaceParams(
    translation: string,
    params: { [key: string]: string | number }
  ): string {
    let result = translation;
    Object.keys(params).forEach((key) => {
      const placeholder = `{{${key}}}`;
      result = result.replace(
        new RegExp(placeholder, 'g'),
        String(params[key])
      );
    });
    return result;
  }

  /**
   * Set current language with lazy loading support
   */
  async setLanguage(languageCode: string): Promise<void> {
    if (this.supportedLanguages.find((lang) => lang.code === languageCode)) {
      // Only update if language actually changed
      if (this.currentLanguageSubject.value !== languageCode) {
        // Load translation file if not already loaded
        if (!this.translations[languageCode]) {
          try {
            await this.loadLanguageFile(languageCode);
          } catch (error) {
            console.error(
              `Failed to load translations for ${languageCode}:`,
              error
            );
            return;
          }
        }

        this.currentLanguageSubject.next(languageCode);

        // Save language preference with expiration
        this.storageService.setItem(
          'user_language',
          languageCode,
          365 * 24 * 60 * 60 * 1000
        ); // 1 year
        this.storageService.setItem('language_change_timestamp', Date.now());

        // Clear cache when language changes
        this.clearTranslationCache();

        // Update document language attribute
        document.documentElement.lang = languageCode;
      }
    } else {
      console.warn(`Language ${languageCode} is not supported`);
    }
  }

  /**
   * Get current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get all supported languages
   */
  getSupportedLanguages(): Language[] {
    return [...this.supportedLanguages];
  }

  /**
   * Check if translations are loaded
   */
  areTranslationsLoaded(): boolean {
    return this.translationsLoaded;
  }

  /**
   * Get translation observable for reactive updates
   */
  getTranslation(
    key: string,
    params?: { [key: string]: string | number }
  ): Observable<string> {
    return new Observable((observer) => {
      // Emit current translation
      observer.next(this.translate(key, params));

      // Subscribe to language changes
      const subscription = this.currentLanguage$.subscribe(() => {
        observer.next(this.translate(key, params));
      });

      // Cleanup subscription
      return () => subscription.unsubscribe();
    });
  }

  /**
   * Get all translations for current language
   */
  getAllTranslations(): { [key: string]: string } {
    const currentLang = this.currentLanguageSubject.value;
    return this.translations[currentLang] || {};
  }

  /**
   * Check if a translation key exists
   */
  hasTranslation(key: string): boolean {
    const currentLang = this.currentLanguageSubject.value;
    return this.translations[currentLang]?.hasOwnProperty(key) || false;
  }

  // ===== ADVANCED TRANSLATION FEATURES FROM OLD CODEBASE =====

  /**
   * Get translated value for a specific field from translateModelList
   * This matches the functionality from the old codebase
   * @param item Item with translateModelList
   * @param fieldName Field name to translate (e.g., "Name", "Description")
   * @param fallbackField Fallback field name if translation not found
   * @returns Translated value or fallback value
   */
  getTranslatedValue(
    item: any,
    fieldName: string,
    fallbackField?: string
  ): string {
    if (!item || !fieldName || !this.getCurrentLanguage()) {
      return this.getFallbackValue(item, fieldName, fallbackField);
    }

    // Check if item has translateModelList
    if (item.translateModelList && Array.isArray(item.translateModelList)) {
      const translation = item.translateModelList.find(
        (t: TranslationModel) =>
          t.languageCode === this.getCurrentLanguage() &&
          t.fieldName === fieldName
      );

      if (translation?.translatedValue) {
        return translation.translatedValue;
      }
    }

    return this.getFallbackValue(item, fieldName, fallbackField);
  }

  /**
   * Get fallback value when translation is not available
   * @param item Item object
   * @param fieldName Primary field name
   * @param fallbackField Alternative field name
   * @returns Fallback value
   */
  private getFallbackValue(
    item: any,
    fieldName: string,
    fallbackField?: string
  ): string {
    if (!item) return '';

    // Try to get value from the field name (convert to camelCase)
    const camelCaseField = this.toCamelCase(fieldName);
    if (item[camelCaseField]) {
      return item[camelCaseField];
    }

    // Try fallback field if provided
    if (fallbackField && item[fallbackField]) {
      return item[fallbackField];
    }

    // Try specific field mappings based on item type
    // For addons
    if (item.addOnName) return item.addOnName;

    // For variants/toppings
    if (item.variantName) return item.variantName;

    // For categories and products
    if (item.name) return item.name;

    // For general items
    if (item.title) return item.title;
    if (item.description) return item.description;
    if (item.shortDescription) return item.shortDescription;

    return '';
  }

  /**
   * Convert field name to camelCase
   * @param fieldName Field name to convert
   * @returns camelCase field name
   */
  private toCamelCase(fieldName: string): string {
    return fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
  }

  /**
   * Get translated name specifically (common use case)
   * @param item Item with translateModelList
   * @returns Translated name or default name
   */
  getTranslatedName(item: any): string {
    return this.getTranslatedValue(item, 'Name', 'name');
  }

  /**
   * Get translated description specifically (common use case)
   * @param item Item with translateModelList
   * @returns Translated description or default description
   */
  getTranslatedDescription(item: any): string {
    return this.getTranslatedValue(item, 'Description', 'shortDescription');
  }

  /**
   * Get translated value for nested object properties
   * @param item Item with translateModelList
   * @param fieldPath Dot-separated path to the field (e.g., "checkout.title")
   * @param fallbackField Fallback field name if translation not found
   * @returns Translated value or fallback value
   */
  getTranslatedNestedValue(
    item: any,
    fieldPath: string,
    fallbackField?: string
  ): string {
    if (!item || !fieldPath || !this.getCurrentLanguage()) {
      return this.getNestedFallbackValue(item, fieldPath, fallbackField);
    }

    // Check if item has translateModelList
    if (item.translateModelList && Array.isArray(item.translateModelList)) {
      const translation = item.translateModelList.find(
        (t: TranslationModel) =>
          t.languageCode === this.getCurrentLanguage() &&
          t.fieldName === fieldPath
      );

      if (translation?.translatedValue) {
        return translation.translatedValue;
      }
    }

    return this.getNestedFallbackValue(item, fieldPath, fallbackField);
  }

  /**
   * Get nested fallback value when translation is not available
   * @param item Item object
   * @param fieldPath Dot-separated path to the field
   * @param fallbackField Alternative field name
   * @returns Fallback value
   */
  private getNestedFallbackValue(
    item: any,
    fieldPath: string,
    fallbackField?: string
  ): string {
    if (!item) return '';

    // Try to get nested value
    const value = this.getNestedValue(item, fieldPath);
    if (value) return value;

    // Try fallback field if provided
    if (fallbackField) {
      const fallbackValue = this.getNestedValue(item, fallbackField);
      if (fallbackValue) return fallbackValue;
    }

    return '';
  }

  /**
   * Get nested value from object using dot notation
   * @param obj Object to get value from
   * @param path Dot-separated path
   * @returns Value at path or empty string
   */
  private getNestedValue(obj: any, path: string): string {
    return (
      path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : '';
      }, obj) || ''
    );
  }

  // ===== STORAGE MANAGEMENT METHODS =====

  /**
   * Get saved language with validation
   */
  private getSavedLanguage(): string {
    const savedLanguage = this.storageService.getItem<string>(
      'user_language',
      'en'
    );

    // Validate that the saved language is supported
    if (
      savedLanguage &&
      this.supportedLanguages.some((lang) => lang.code === savedLanguage)
    ) {
      return savedLanguage;
    }

    // Fallback to browser language if supported
    const browserLang = navigator.language.split('-')[0];
    if (this.supportedLanguages.some((lang) => lang.code === browserLang)) {
      return browserLang;
    }

    // Final fallback to English
    return 'en';
  }

  /**
   * Get user language preferences and history
   */
  getLanguagePreferences(): {
    current: string;
    lastChanged: number | null;
    isDefault: boolean;
  } {
    const current = this.getCurrentLanguage();
    const lastChanged = this.storageService.getItem<number>(
      'language_change_timestamp'
    );
    const savedLanguage = this.storageService.getItem<string>('user_language');

    return {
      current,
      lastChanged,
      isDefault: !savedLanguage || savedLanguage === 'en',
    };
  }

  // ===== CACHE MANAGEMENT METHODS =====

  /**
   * Set cached translation with size management
   */
  private setCachedTranslation(cacheKey: string, value: string): void {
    // If cache is at max size, remove oldest entries (LRU)
    if (this.translationCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.translationCache.keys().next().value;
      if (firstKey) {
        this.translationCache.delete(firstKey);
      }
    }

    this.translationCache.set(cacheKey, value);
  }

  /**
   * Clear translation cache (public method for debugging)
   */
  clearTranslationCache(): void {
    this.translationCache.clear();
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  getCacheStats(): {
    hits: number;
    misses: number;
    size: number;
    hitRate: number;
  } {
    const total = this.cacheHits + this.cacheMisses;
    return {
      hits: this.cacheHits,
      misses: this.cacheMisses,
      size: this.translationCache.size,
      hitRate: total > 0 ? (this.cacheHits / total) * 100 : 0,
    };
  }

  /**
   * Preload translations for specific languages
   */
  async preloadLanguages(languageCodes: string[]): Promise<void> {
    const loadPromises = languageCodes
      .filter((code) =>
        this.supportedLanguages.some((lang) => lang.code === code)
      )
      .map((code) => this.loadLanguageFile(code));

    await Promise.all(loadPromises);
  }

  /**
   * Unload unused translations to free memory
   */
  unloadUnusedTranslations(): void {
    const currentLang = this.getCurrentLanguage();
    const keysToDelete = Object.keys(this.translations).filter(
      (lang) => lang !== currentLang
    );

    keysToDelete.forEach((lang) => {
      if (lang) {
        delete this.translations[lang];
        console.log(`🗑️ Unloaded translations for ${lang}`);
      }
    });

    this.clearTranslationCache();
  }
}
